import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { AccountBalanceWallet, SouthWest, NorthEast, Savings } from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useWalletReport } from '../hooks';
import { colors } from '../theme/theme';

const fmt = (n?: number) => `KES ${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const WalletReportPage = () => {
  const [period, setPeriod] = useState<string>('this_month');
  const { report, isLoading, error } = useWalletReport({ date_filter: period });

  const obligation = report?.obligation;
  const summary = report?.period_summary;

  const activityRows = (report?.recent_activity ?? []).map((a) => ({
    id: a.id,
    reference: a.reference,
    type: a.type,
    amount: a.amount,
    customer: a.customer,
    date: a.date,
  }));

  const columns: GridColDef[] = [
    { field: 'reference', headerName: 'Reference', flex: 1, minWidth: 160 },
    {
      field: 'type',
      headerName: 'Type',
      width: 130,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value === 'top_up' ? 'Top-up' : 'Spend'}
          color={p.value === 'top_up' ? 'info' : 'success'}
          variant="outlined"
        />
      ),
    },
    { field: 'customer', headerName: 'Customer', flex: 1, minWidth: 160 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      renderCell: (p) => <span>{fmt(p.value)}</span>,
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 190,
      renderCell: (p) => <span>{p.value ? new Date(p.value).toLocaleString() : '—'}</span>,
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Wallet Report"
          subtitle="Customer wallet balances are a county obligation — money held on behalf of customers."
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Reports' },
            { label: 'Wallet' },
          ]}
        />

        {/* Period filter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <ToggleButtonGroup
              value={period}
              exclusive
              size="small"
              onChange={(_e, v) => v && setPeriod(v)}
            >
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="this_week">This Week</ToggleButton>
              <ToggleButton value="this_month">This Month</ToggleButton>
              <ToggleButton value="this_year">This Year</ToggleButton>
              <ToggleButton value="all">All Time</ToggleButton>
            </ToggleButtonGroup>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load the wallet report. Please try again.
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Obligation highlight — the headline liability figure */}
            <Card sx={{ mb: 3, border: `2px solid ${colors.warning.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Savings sx={{ color: colors.warning.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Outstanding wallet obligation
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: colors.warning.main }}>
                  {fmt(obligation?.outstanding)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Money customers have loaded but not yet spent. The county is holding this on their
                  behalf — it is a liability, not revenue. (Lifetime topped up {fmt(obligation?.topped_up)} −
                  spent {fmt(obligation?.spent)}.)
                </Typography>
              </CardContent>
            </Card>

            {/* Period flow stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Topped up (period)"
                  value={fmt(summary?.topped_up)}
                  icon={<SouthWest />}
                  color={colors.info.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Spent (period)"
                  value={fmt(summary?.spent)}
                  icon={<NorthEast />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Net change (period)"
                  value={fmt(summary?.net_change)}
                  icon={<AccountBalanceWallet />}
                  color={colors.warning.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Wallet transactions"
                  value={((summary?.topup_count ?? 0) + (summary?.spend_count ?? 0)).toLocaleString()}
                  icon={<AccountBalanceWallet />}
                  color={colors.primary.main}
                />
              </Grid>
            </Grid>

            {/* Top-up vs spend trend */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Top-ups vs spends
                </Typography>
                {(report?.daily_trend?.length ?? 0) === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No wallet activity in this period.
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={report?.daily_trend ?? []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(v: any) => fmt(Number(v))} />
                      <Legend />
                      <Bar dataKey="topped_up" name="Topped up" fill={colors.info.main} radius={[6, 6, 0, 0]} />
                      <Bar dataKey="spent" name="Spent" fill={colors.success.main} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Recent activity */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Recent wallet activity
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    rows={activityRows}
                    columns={columns}
                    autoHeight
                    pageSizeOptions={[10, 25]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    slots={{ toolbar: GridToolbar }}
                    disableRowSelectionOnClick
                  />
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </AdminLayout>
  );
};
